import React from 'react';
import { FileImage, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { UploadedFile } from '../../types/detection';

interface FileListProps {
  files: UploadedFile[];
  onRemoveFile: (id: string) => void;
  onClearAll: () => void;
  onFileClick: (id: string) => void;
  activeFileId?: string;
  isProcessing: boolean;
  formatFileSize: (bytes: number) => string;
}

export const FileList: React.FC<FileListProps> = ({
  files,
  onRemoveFile,
  onClearAll,
  onFileClick,
  activeFileId,
  isProcessing,
  formatFileSize,
}) => {
  if (files.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <FileImage className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No files uploaded</p>
        <p className="text-xs">Drag and drop or click above</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Uploaded Files ({files.length})
        </h4>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs px-2"
          onClick={onClearAll}
          disabled={isProcessing}
        >
          Clear All
        </Button>
      </div>

      <div className="space-y-1.5">
        {files.map((uploadedFile) => (
          <FileListItem
            key={uploadedFile.id}
            file={uploadedFile}
            onRemove={() => onRemoveFile(uploadedFile.id)}
            onClick={() => (uploadedFile.status === 'done' || uploadedFile.status === 'pending') && onFileClick(uploadedFile.id)}
            isActive={activeFileId === uploadedFile.id}
            formatFileSize={formatFileSize}
          />
        ))}
      </div>
    </div>
  );
};

interface FileListItemProps {
  file: UploadedFile;
  onRemove: () => void;
  onClick: () => void;
  isActive: boolean;
  formatFileSize: (bytes: number) => string;
}

const FileListItem: React.FC<FileListItemProps> = ({
  file,
  onRemove,
  onClick,
  isActive,
  formatFileSize,
}) => {
  const statusConfig = {
    pending: {
      icon: <FileImage className="w-3.5 h-3.5 text-muted-foreground" />,
      bg: 'bg-muted/30 border-border hover:bg-muted/50 dark:bg-gray-800/50 dark:border-gray-700 dark:hover:bg-gray-700/50 cursor-pointer',
    },
    processing: {
      icon: <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />,
      bg: 'bg-blue-50 border-blue-200 animate-pulse dark:bg-blue-900/30 dark:border-blue-800',
    },
    done: {
      icon: <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />,
      bg: 'bg-green-50 border-green-200 hover:bg-green-100/70 cursor-pointer dark:bg-green-900/30 dark:border-green-800 dark:hover:bg-green-900/50',
    },
    error: {
      icon: <AlertCircle className="w-3.5 h-3.5 text-red-500" />,
      bg: 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800',
    },
  };

  const config = statusConfig[file.status];

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 p-2 rounded-md border transition-all duration-200 group",
        config.bg,
        isActive && "ring-2 ring-orange-500 ring-offset-1 dark:ring-offset-gray-900"
      )}
    >
      <div className="flex-shrink-0">{config.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate text-foreground" title={file.file.name}>
          {file.file.name}
        </p>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground">
            {formatFileSize(file.file.size)}
          </span>
          {file.result && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1 font-mono">
              {file.result.results.total_detections} det
            </Badge>
          )}
          {file.error && (
            <span className="text-[10px] text-red-600 truncate">{file.error}</span>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        disabled={file.status === 'processing'}
      >
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
};
